import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import SendTestTelegramMessageModal from '@/components/admin/telegram/SendTestTelegramMessageModal';
import FullscreenError from '@/components/utils/FullscreenError';
import { faComments, faSpinner, faArrowLeft, faPlus, faEye, faExclamationTriangle, faCheckCircle, faClock } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, shortObjectID } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { TelegramMessage, } from '@/models';
import { TelegramMessageStatus } from '@/models';

export default function AdminTelegramMessagesList() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<TelegramMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const [isTestMessageModalOpen, setIsTestMessageModalOpen] = useState(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<TelegramMessageStatus | 'ALL'>('ALL');

  useEffect(() => {
    fetchMessages();
  }, [currentPage, statusFilter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listTelegramMessages({
        limit: pageSize,
        next: cursorToken,
        status: statusFilter === 'ALL' ? undefined : statusFilter
      });
      setMessages(response.messages);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading Telegram messages list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Telegram-Nachrichten');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestMessageSent = () => {
    fetchMessages();
  };

  const handleFilterChange = (newStatus: TelegramMessageStatus | 'ALL') => {
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const getStatusIcon = (status: TelegramMessageStatus) => {
    switch (status) {
      case 'WAITING':
        return <FontAwesomeIcon icon={faClock} className="w-4 h-4 text-yellow-600" />;
      case 'SUCCESS':
        return <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4 text-green-600" />;
      case 'FAILED':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: TelegramMessageStatus) => {
    switch (status) {
      case 'WAITING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400';
      case 'SUCCESS':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400';
      case 'FAILED':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Telegram-Nachrichten nicht verfügbar"
        message={error || 'Die Telegram-Nachrichten-Liste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchMessages}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Telegram-Nachrichten ({total})
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsTestMessageModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
              <span>Test-Nachricht senden</span>
            </button>
          </div>
        </div>

        {/* Filter section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-4 self-center">Status:</span>
            {(['ALL', TelegramMessageStatus.WAITING, TelegramMessageStatus.SUCCESS, TelegramMessageStatus.FAILED] as const).map((status) => (
              <button
                key={status}
                onClick={() => handleFilterChange(status)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                  statusFilter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {status === 'ALL' ? 'Alle' : status}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {messages.length > 0 && !loading && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Chat ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Inhalt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Nachrichten-ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Erstellt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {messages.map((message) => (
                    <tr key={message.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(message.status)}`}>
                          {getStatusIcon(message.status)}
                          <span className="ml-1">{message.status}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white font-mono">
                          {message.chatID}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {message.content}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                          {shortObjectID(message.id)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(message.created)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <Link
                          to={`/admin/telegram-messages/${message.id}`}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center"
                        >
                          <FontAwesomeIcon icon={faEye} className="w-4 h-4 mr-1" />
                          Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              loading={loading}
            />
          </div>
        )}

        {messages.length === 0 && !loading && !error && (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faComments} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">Keine Telegram-Nachrichten gefunden</p>
          </div>
        )}

      <SendTestTelegramMessageModal
        isOpen={isTestMessageModalOpen}
        onClose={() => setIsTestMessageModalOpen(false)}
        onTestMessageSent={handleTestMessageSent}
      />
    </>
  );
}
